/**
 * Administrare de secțiune în consola de platformă.
 *
 * Nu se încarcă pagina restaurantului. Lista este a consolei, cu Șterge mereu vizibil.
 * Modifică deschide setările acelei secțiuni: ecranul (orientare, zonă, mod), lista de
 * redare (tranziție și elemente), meniul (comandă și publicare).
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, InlineAlert, useToast } from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { errorMessage, useSession } from '../../lib/session.js';
import { AdminCard, AdminHeader, AdminRow, AdminTable, Td } from './AdminKit.js';

interface OperateRestaurant {
  id: string;
  name: string;
  account_id: string;
  account_name: string;
}

interface Row {
  id: string;
  name?: string;
  label?: string;
  code?: string;
  email?: string;
  status?: string;
  restaurant_name?: string;
  zone?: string | null;
  orientation?: string;
  content_mode?: string;
  ordering_mode?: string;
  transition?: string;
  is_active?: boolean;
  is_available?: boolean;
  items?: Array<{ id: string; item_type: string; title: string | null; duration_seconds: number }>;
  item_count?: number;
  price_cents?: number;
}

const STORAGE_KEY = 'menuvia-admin-operate-account';

const TITLES: Record<string, string> = {
  menus: 'Meniuri',
  products: 'Produse',
  qr: 'Coduri QR',
  displays: 'Ecrane',
  playlists: 'Liste de redare',
  orders: 'Comenzi',
  promotions: 'Promoții',
  locations: 'Locații',
  team: 'Echipă',
  analytics: 'Statistici',
};

function labelOf(row: Row): string {
  return row.name || row.label || row.code || row.email || row.id;
}

export function AdminOperate() {
  const session = useSession();
  const toast = useToast();
  const { section = 'menus' } = useParams();
  const [restaurants, setRestaurants] = useState<OperateRestaurant[]>([]);
  const [search, setSearch] = useState('');
  const [resultTotal, setResultTotal] = useState(0);
  const [picked, setPicked] = useState<OperateRestaurant | null>(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as OperateRestaurant) : null;
    } catch {
      return null;
    }
  });
  const [ready, setReady] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Row | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Row | null>(null);
  const [draftName, setDraftName] = useState('');

  const accountId = picked?.account_id ?? '';

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const q = search.trim();
      api
        .getRaw<{ data: OperateRestaurant[]; meta?: { total: number } }>(
          `/admin/restaurants?limit=20${q ? `&q=${encodeURIComponent(q)}` : ''}`,
        )
        .then((response) => {
          setRestaurants(response.data ?? []);
          setResultTotal(response.meta?.total ?? response.data?.length ?? 0);
        })
        .catch(() => setError('Căutarea restaurantelor nu a reușit.'));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    if (!picked) return;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(picked));
    if (session.activeAccountId === picked.account_id) {
      setReady(true);
      return;
    }
    let cancelled = false;
    setReady(false);
    session
      .switchAccount(picked.account_id)
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch(() => {
        if (!cancelled) setError('Restaurantul nu a putut fi deschis.');
      });
    return () => {
      cancelled = true;
    };
  }, [picked, session.activeAccountId, session]);

  const load = useCallback(async () => {
    if (!ready) return;
    setLoading(true);
    setError(null);
    try {
      if (section === 'displays') {
        const response = await api.getRaw<{ data: Row[] }>('/displays');
        setRows(response.data ?? []);
      } else if (section === 'qr') {
        const response = await api.getRaw<{ data: Row[] }>('/qr?days=7');
        setRows(response.data ?? []);
      } else if (section === 'analytics') {
        setRows([]);
      } else {
        const path =
          section === 'menus'
            ? '/menus'
            : section === 'products'
              ? '/products'
              : section === 'playlists'
                ? '/playlists'
                : section === 'promotions'
                  ? '/promotions'
                  : section === 'locations'
                    ? '/locations'
                    : section === 'orders'
                      ? '/orders'
                      : section === 'team'
                        ? '/members'
                        : '';
        if (!path) setRows([]);
        else {
          const data = await api.get<Row[] | { members?: Row[] }>(path);
          if (Array.isArray(data)) setRows(data);
          else setRows(data.members ?? []);
        }
      }
    } catch (err) {
      setRows([]);
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [ready, section]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(id: string, job: () => Promise<void>, ok: string) {
    setBusyId(id);
    try {
      await job();
      toast.success(ok);
      setEditing(null);
      setPendingDelete(null);
      await load();
    } catch (err) {
      toast.error('Acțiunea nu a reușit', errorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  async function openEditor(row: Row) {
    if (section === 'playlists') {
      const detail = await api.get<Row>(`/playlists/${row.id}`);
      setEditing({ ...row, ...detail });
      return;
    }
    setEditing({ ...row });
  }

  async function saveEditor() {
    if (!editing) return;
    if (section === 'displays') {
      await run(editing.id, () =>
        api.patch(`/displays/${editing.id}`, {
          name: editing.name,
          zone: editing.zone || null,
          orientation: editing.orientation,
          contentMode: editing.content_mode,
        }),
      'Setările ecranului au fost salvate');
      return;
    }
    if (section === 'playlists') {
      await run(editing.id, () =>
        api.patch(`/playlists/${editing.id}`, {
          name: editing.name,
          transition: editing.transition,
          isActive: editing.is_active,
        }),
      'Lista de redare a fost salvată');
      return;
    }
    if (section === 'menus') {
      await run(editing.id, () =>
        api.patch(`/menus/${editing.id}`, {
          name: editing.name,
          orderingMode: editing.ordering_mode,
        }),
      'Meniul a fost salvat');
      return;
    }
    if (section === 'qr') {
      await run(editing.id, () => api.patch(`/qr/${editing.id}`, { label: editing.label, status: editing.status }), 'Codul QR a fost salvat');
      return;
    }
    await run(editing.id, () => api.patch(`/${section === 'products' ? 'products' : section === 'locations' ? 'locations' : section}/${editing.id}`, { name: editing.name }), 'Salvat');
  }

  async function remove(row: Row) {
    const path =
      section === 'menus'
        ? `/menus/${row.id}`
        : section === 'displays'
          ? `/displays/${row.id}`
          : section === 'products'
            ? `/products/${row.id}`
            : section === 'qr'
              ? `/qr/${row.id}`
              : section === 'playlists'
                ? `/playlists/${row.id}`
                : section === 'promotions'
                  ? `/promotions/${row.id}`
                  : section === 'team'
                    ? `/members/${row.id}`
                    : '';
    if (!path) return;
    await run(row.id, () => api.del(path), 'Șters');
  }

  async function addPlaylistItem() {
    if (!editing) return;
    await run(editing.id, async () => {
      await api.post(`/playlists/${editing.id}/items`, { itemType: 'text', title: 'Element nou', durationSeconds: 12 });
      const detail = await api.get<Row>(`/playlists/${editing.id}`);
      setEditing({ ...editing, ...detail });
    }, 'Element adăugat');
  }

  async function removePlaylistItem(itemId: string) {
    if (!editing) return;
    await run(itemId, async () => {
      await api.del(`/playlists/${editing.id}/items/${itemId}`);
      const detail = await api.get<Row>(`/playlists/${editing.id}`);
      setEditing({ ...editing, ...detail });
    }, 'Element șters');
  }

  async function createMenu() {
    if (!picked || draftName.trim().length < 2) return;
    await run('create', () => api.post('/menus', { restaurantId: picked.id, name: draftName.trim(), kind: 'custom' }), 'Meniu creat');
    setDraftName('');
  }

  const title = TITLES[section] ?? 'Administrare';

  return (
    <div className="flex flex-col gap-4">
      <AdminHeader title={title} subtitle="Editorul consolei: setările secțiunii și ștergerea, fără pagina restaurantului." />

      <label className="flex max-w-xl flex-col gap-1 text-caption text-ink-300">
        Caută restaurant
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Nume, slug sau cont"
          className="h-10 rounded-[var(--radius-md)] border border-white/15 bg-ink-950 px-3 text-body-s text-white"
        />
      </label>
      {picked ? (
        <p className="text-body-s text-white">
          Selectat: <span className="font-medium">{picked.name}</span>
          <span className="text-ink-400"> · {picked.account_name}</span>
        </p>
      ) : (
        <p className="text-caption text-ink-400">Alege un restaurant din rezultate.</p>
      )}
      <ul className="flex max-w-xl flex-col gap-1">
        {restaurants.map((restaurant) => (
          <li key={restaurant.id}>
            <button
              type="button"
              onClick={() => setPicked(restaurant)}
              className={`w-full rounded-[var(--radius-md)] px-3 py-2 text-left text-body-s ${
                picked?.id === restaurant.id ? 'bg-white/15 text-white' : 'text-ink-200 hover:bg-white/5'
              }`}
            >
              {restaurant.name}
              <span className="block text-caption text-ink-400">{restaurant.account_name}</span>
            </button>
          </li>
        ))}
      </ul>
      <p className="text-caption text-ink-400">
        {resultTotal > restaurants.length
          ? `${restaurants.length} din ${resultTotal}. Continuă să scrii ca să restrângi lista.`
          : `${resultTotal} restaurante găsite.`}
      </p>

      {error ? <InlineAlert tone="danger" title="Nu am putut încărca datele">{error}</InlineAlert> : null}

      {section === 'menus' && ready ? (
        <AdminCard>
          <div className="flex flex-wrap gap-2">
            <input
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
              placeholder="Nume meniu nou"
              className="h-10 min-w-[200px] flex-1 rounded-[var(--radius-md)] border border-white/15 bg-ink-950 px-3 text-body-s text-white"
            />
            <Button size="sm" loading={busyId === 'create'} onClick={() => void createMenu()}>
              Adaugă meniu
            </Button>
          </div>
        </AdminCard>
      ) : null}

      {editing ? (
        <AdminCard>
          <h3 className="mb-3 font-medium text-white">Setări — {labelOf(editing)}</h3>
          <div className="flex flex-col gap-3">
            {section !== 'qr' ? (
              <label className="flex flex-col gap-1 text-caption text-ink-300">
                Nume
                <input
                  value={editing.name ?? ''}
                  onChange={(event) => setEditing({ ...editing, name: event.target.value })}
                  className="h-10 rounded-[var(--radius-md)] border border-white/15 bg-ink-950 px-3 text-body-s text-white"
                />
              </label>
            ) : (
              <label className="flex flex-col gap-1 text-caption text-ink-300">
                Etichetă
                <input
                  value={editing.label ?? ''}
                  onChange={(event) => setEditing({ ...editing, label: event.target.value })}
                  className="h-10 rounded-[var(--radius-md)] border border-white/15 bg-ink-950 px-3 text-body-s text-white"
                />
              </label>
            )}

            {section === 'displays' ? (
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="flex flex-col gap-1 text-caption text-ink-300">
                  Zonă
                  <input
                    value={editing.zone ?? ''}
                    onChange={(event) => setEditing({ ...editing, zone: event.target.value })}
                    className="h-10 rounded-[var(--radius-md)] border border-white/15 bg-ink-950 px-3 text-body-s text-white"
                  />
                </label>
                <label className="flex flex-col gap-1 text-caption text-ink-300">
                  Orientare
                  <select
                    value={editing.orientation ?? 'landscape'}
                    onChange={(event) => setEditing({ ...editing, orientation: event.target.value })}
                    className="h-10 rounded-[var(--radius-md)] border border-white/15 bg-ink-950 px-3 text-body-s text-white"
                  >
                    <option value="landscape">Orizontal</option>
                    <option value="portrait">Vertical</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-caption text-ink-300">
                  Conținut
                  <select
                    value={editing.content_mode ?? 'menu'}
                    onChange={(event) => setEditing({ ...editing, content_mode: event.target.value })}
                    className="h-10 rounded-[var(--radius-md)] border border-white/15 bg-ink-950 px-3 text-body-s text-white"
                  >
                    <option value="menu">Meniul sălii</option>
                    <option value="kitchen">Bucătărie</option>
                  </select>
                </label>
              </div>
            ) : null}

            {section === 'playlists' ? (
              <div className="flex flex-col gap-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1 text-caption text-ink-300">
                    Tranziție
                    <select
                      value={editing.transition ?? 'fade'}
                      onChange={(event) => setEditing({ ...editing, transition: event.target.value })}
                      className="h-10 rounded-[var(--radius-md)] border border-white/15 bg-ink-950 px-3 text-body-s text-white"
                    >
                      <option value="fade">Estompare</option>
                      <option value="slide">Alunecare</option>
                      <option value="none">Fără tranziție</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-2 pt-5 text-body-s text-white">
                    <input
                      type="checkbox"
                      checked={editing.is_active !== false}
                      onChange={(event) => setEditing({ ...editing, is_active: event.target.checked })}
                    />
                    Listă activă
                  </label>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-caption text-ink-300">Elemente pe ecran</span>
                  <Button size="sm" variant="secondary" onClick={() => void addPlaylistItem()}>
                    Adaugă element
                  </Button>
                </div>
                <ul className="flex flex-col gap-2">
                  {(editing.items ?? []).map((item) => (
                    <li key={item.id} className="flex items-center justify-between gap-2 rounded-[var(--radius-md)] border border-white/10 px-3 py-2">
                      <span className="text-body-s text-white">
                        {item.title || item.item_type} · {item.duration_seconds}s
                      </span>
                      <Button size="sm" variant="secondary" onClick={() => void removePlaylistItem(item.id)}>
                        Șterge element
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {section === 'menus' ? (
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1 text-caption text-ink-300">
                  Comenzi
                  <select
                    value={editing.ordering_mode ?? 'view_only'}
                    onChange={(event) => setEditing({ ...editing, ordering_mode: event.target.value })}
                    className="h-10 rounded-[var(--radius-md)] border border-white/15 bg-ink-950 px-3 text-body-s text-white"
                  >
                    <option value="view_only">Doar vizualizare</option>
                    <option value="dine_in">La masă</option>
                    <option value="online">Ridicare și livrare</option>
                  </select>
                </label>
                <Button size="sm" variant="secondary" loading={busyId === editing.id} onClick={() => void run(editing.id, () => api.post(`/menus/${editing.id}/publish`), 'Meniu publicat')}>
                  Publică
                </Button>
              </div>
            ) : null}

            <div className="flex gap-2">
              <Button size="sm" loading={busyId === editing.id} onClick={() => void saveEditor()}>
                Salvează setările
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setEditing(null)}>
                Închide
              </Button>
            </div>
          </div>
        </AdminCard>
      ) : null}

      <AdminCard>
        {loading ? (
          <p className="text-body-s text-ink-300">Se încarcă…</p>
        ) : !ready || !accountId ? (
          <p className="text-body-s text-ink-300">Selectează un restaurant.</p>
        ) : section === 'analytics' ? (
          <p className="text-body-s text-ink-300">Statisticile rămân în rapoartele restaurantului. Editarea se face la meniuri, ecrane și liste de redare.</p>
        ) : rows.length === 0 ? (
          <p className="text-body-s text-ink-300">Nicio înregistrare.</p>
        ) : (
          <AdminTable headers={['Nume', 'Detaliu', '']}>
            {rows.map((row) => (
              <AdminRow key={row.id}>
                <Td>
                  <span className="font-medium text-white">{labelOf(row)}</span>
                </Td>
                <Td>
                  <span className="text-caption text-ink-400">
                    {[row.status, row.content_mode, row.ordering_mode, row.transition, row.restaurant_name].filter(Boolean).join(' · ') || '—'}
                  </span>
                </Td>
                <Td className="text-right">
                  {pendingDelete?.id === row.id ? (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" loading={busyId === row.id} onClick={() => void remove(row)}>
                        Confirmă ștergerea
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setPendingDelete(null)}>
                        Renunță
                      </Button>
                    </div>
                  ) : (
                    <div className="flex justify-end gap-2">
                      {section !== 'orders' && section !== 'team' && section !== 'promotions' ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => void openEditor(row)}
                        >
                          Modifică
                        </Button>
                      ) : null}
                      {section !== 'orders' && section !== 'locations' ? (
                        <Button size="sm" variant="secondary" onClick={() => setPendingDelete(row)}>
                          Șterge
                        </Button>
                      ) : null}
                    </div>
                  )}
                </Td>
              </AdminRow>
            ))}
          </AdminTable>
        )}
      </AdminCard>
    </div>
  );
}
